import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { SecretInputComponent } from '../../secret-input/secret-input.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { ConnectorForm, InBodyConfiguration } from './in-body-connector-dialog.models';

@Component({
  selector: 'blaze-in-body-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    MatIconModule,
    MatSlideToggleModule,
    PanelComponent,
    ReactiveFormsModule,
    SecretInputComponent,
    WebhookIdInputComponent,
  ],
  templateUrl: './in-body-connector-dialog.component.html',
  styleUrls: ['./in-body-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class InBodyConnectorDialogComponent
  extends BaseConnectorDialogComponent<InBodyConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/InBody/webhook';

  serviceName = 'InBody';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();

    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      webhookId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      secret: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    });
  }

  ngOnInit(): void {
    this.fillForm(this.getConfiguration(this.connector));
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, webhookId, secret } = this.connectorForm.value;

    if (!connectorName || !webhookId || !secret) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        id: webhookId,
        secret,
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): InBodyConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const id = String(configuration?.id ?? '');
    const secret = String(configuration?.secret ?? '');

    return {
      connectorId,
      connectorName,
      id,
      secret,
    };
  }

  fillForm(configuration: InBodyConfiguration): void {
    const { connectorId, connectorName, id, secret } = configuration;
    const webhookId = id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        webhookId,
        secret,
      },
      { emitEvent: false },
    );
  }
}
